import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { AppState } from '@chegg-wt/core/state'
import { NOOP } from '@chegg-wt/core/constants/global'
import {
  styleIdToAlias,
  aliasToStyleId,
  isStyleFootnote,
} from '@chegg-wt/core/utils/styles'
import { Citation as CitationType } from '@chegg-wt/core/types/redux-citation-api'
import { useQuery, useMutation } from '@apollo/client'
import { GetProjectVariables, GetProject } from '@chegg-wt/core/queries/GetProject'
import { GET_PROJECT, UPDATE_PROJECT } from '@chegg-wt/core/queries/Project'
import { CITATION_COUNT, DELETE_CITATION } from '@chegg-wt/core/queries/Citations'
import {
  getIsNotFoundErrorByMessage,
  mapFormattedCitationsV2toCitation,
} from '@chegg-wt/core/queries/utils'
import {
  UpdateProjectVariables,
  UpdateProject,
} from '@chegg-wt/core/queries/UpdateProject'
import {
  DeleteCitation,
  DeleteCitationVariables,
} from '@chegg-wt/core/queries/DeleteCitation'
import { ValidatorStates } from '@chegg-wt/core/state/citation/constants'
import { ValidatorState, CitationStatus } from '@chegg-wt/core/state/citation/types'
import { useToast } from '@chegg-wt/core/hooks/useToast'
import { SuccessToast } from '@chegg-wt/core/components/Toast'
import { ExportModal } from '@chegg-wt/core/components/Modals/ExportModal'

import {
  CITATION_BREADCRUMB_PLURAL,
  CITATION_BREADCRUMB_SINGLE,
} from '@chegg-wt/core/constants/citations'
import Error from 'next/error'
import RouterPagination from '@chegg-wt/core/components/RouterPagination'
import { useRouter } from 'next/router'
import {
  getCitationCount,
  getMaxCitationPages,
  getShowPagination,
} from '@chegg-wt/core/utils/citations'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import { Citation } from '@chegg-wt/core/components/BibliographyContainer/CitationList/Citation'
import { CheckboxMenu } from '@chegg-wt/core/components/BibliographyContainer/CheckboxMenu'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { CITATIONS_DASHBOARD } from '@chegg-wt/core/constants/citationFlow'
import { NEW_PROJECT_ID, SortKeys, SortKey } from '@chegg-wt/core/constants/projects'
import { copyAll } from '@chegg-wt/core/utils/copy'
import { sortCitationsByKey, newProjectName } from '@chegg-wt/core/utils/projects'
import { getProjectsExperience } from '@chegg-wt/core/utils/citationFlow'
import { navigated } from '@chegg-wt/core/state/app/actions'
import {
  setCurrentProject,
  setCurrentProjectStyleName,
  fetchCurrentProjectStyleName,
} from '@chegg-wt/core/state/currentProject/actions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import {
  initializedSelector,
  updateMessagingFlagSelector,
} from '@chegg-wt/core/state/app/selectors'
import {
  currentStyleSelector,
  currentStyleNameSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import { stylesSelector } from '@chegg-wt/core/state/styles/selectors'
import { isProSelector, loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { GET_FORMATTED_CITATIONSV2 } from '@chegg-wt/core/oneGraphQueries/FormattedCitations'
import {
  GetFormattedCitationsV2,
  GetFormattedCitationsV2Variables,
} from '../../oneGraphQueries/__generated__/GetFormattedCitationsV2'
import { useRoutesContext } from '../RoutesContext'
import { BreadcrumbPart, Breadcrumbs } from '../Breadcrumbs'
import PremiumCopyModal from '../Modals/PremiumCopyModal'
import {
  DeleteCitationsModal,
  deleteCitationsTitle,
  deleteCitationsDescription,
} from '../Modals/DeleteModal/DeleteCitationsModal'
import { ExportResult, ExportType } from '../Modals/ExportModal/types'
import { SubHeadingBCRest } from '../BrandConsolidation'
import { PageHeader } from '../Layouts/styled'
import * as Styled from './styled'
import CitationFlowDrawer from './CitationFlowDrawer'
import CitationListCTAContainer from './CitationListCTAContainer'
import { CitationListEmptyContainer } from './CitationListEmptyContainer'
import { CitationListHeaderContainer } from './CitationListHeaderContainer'
import {
  CitationListProps,
  CitationListStateProps,
  CitationListDispatchProps,
} from './types'
import { ERROR_MESSAGE_PROJECT_NOT_FOUND } from './constants'

type Props = CitationListProps & CitationListStateProps & CitationListDispatchProps

export const CitationListContainer: React.FunctionComponent<Props> = ({
  isProUser,
  isLoggedIn,
  initialSourceType,
  initialSearchQuery,
  currentStyle: initialStyle,
  currentStyleName,
  copy,
  projectId,
  flowStep,
  navigated,
  initialized,
  exportTabIds,
  userClick,
  setCurrentProject,
  fetchCurrentProjectStyleName,
  stylesList,
  setCurrentProjectStyleName,
  updateMessagingFeatureEnabled,
}) => {
  const routes = useRoutesContext()
  const isNew = projectId === NEW_PROJECT_ID

  const router = useRouter()
  const { page: routerPage } = router.query as { page: string }

  const [page, setCitationPage] = useState<number>(parseInt(routerPage, 10) || 1)
  const [currentStyle, setCurrentStyle] = useState<string>()
  const [editingCitation, setEditingCitation] = useState<CitationType | undefined>()
  const [lastUpdatedCitation, setLastUpdatedCitation] = useState<
    CitationStatus | undefined
  >()

  const { toastState, showToast: showSuccessToast, hideToast } = useToast()
  const [exportModalCitations, setExportModalCitations] = useState<CitationType[]>([])
  const [deleteModalCitations, setDeleteModalCitations] = useState<CitationType[]>([])
  const [premiumCopyCitation, setPremiumCopyCitation] = useState<CitationType | null>(
    null
  )
  const [selectedCitations, setSelectedCitations] = useState<CitationType[]>([])

  const isFootnote = isStyleFootnote(currentStyle)

  useEffect(() => {
    hideToast()
  }, [projectId])

  const { data: projectData, error: projectError } = useQuery<
    GetProject,
    GetProjectVariables
  >(GET_PROJECT, {
    variables: {
      projectId,
    },
    ssr: false,
    skip: !initialized || !isProUser || isNew,
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (projectData) {
      setCurrentProject(projectData.project)
      fetchCurrentProjectStyleName(projectData.project.defaultStyle)
      setCurrentStyle(projectData.project.defaultStyle)
    } else if (!currentStyle && initialStyle) {
      setCurrentStyle(initialStyle)
    }
  }, [projectData])

  const { data: citationsCountData } = useQuery(CITATION_COUNT, {
    fetchPolicy: 'cache-and-network',
    variables: {
      projectId,
    },
    ssr: false,
    skip: !initialized || !projectData || !currentStyle || !projectId,
  })

  const citationCount = getCitationCount(citationsCountData)
  const showPagination = getShowPagination(citationCount)
  const maxPages = getMaxCitationPages(citationCount)
  const enableSort = !showPagination
  const defaultSortKey = enableSort ? SortKeys.ALPHABETICAL : SortKeys.DATE_MOST_RECENT

  const [sortCitationsKey, setSortCitationsKey] = useState<SortKey>(defaultSortKey)

  const oneGraphGetCitationQueryVariables = {
    citationProjectId: projectId,
    citationStyle: aliasToStyleId(currentStyle),
  }
  const skipFormattedCitationQuery = !initialized || !projectData || !currentStyle
  const {
    data: formattedCitationsDataV2,
    loading: isCitationsLoading,
    refetch: refetchCitations,
  } = useQuery<GetFormattedCitationsV2, GetFormattedCitationsV2Variables>(
    GET_FORMATTED_CITATIONSV2,
    {
      fetchPolicy: 'cache-and-network',
      variables: oneGraphGetCitationQueryVariables,
      context: {
        clientName: 'oneGraph',
      },
      ssr: false,
      skip: skipFormattedCitationQuery,
    }
  )

  const [updateProject] = useMutation<UpdateProject, UpdateProjectVariables>(
    UPDATE_PROJECT
  )

  const [deleteCitation] = useMutation<DeleteCitation, DeleteCitationVariables>(
    DELETE_CITATION
  )

  const setSelectedCitationsFromChangedCitations = (citations: CitationType[]) => {
    // make sure any selected citations still exist
    const newSelectedCitations = selectedCitations.reduce((acc, curr) => {
      if (!citations.some((c) => c.id === curr.id)) {
        return [...acc, curr]
      }
      return acc
    }, [])
    setSelectedCitations(newSelectedCitations)
  }

  const handleClickCreateCitation = () => {
    navigated(routes.toCitationListSource({ projectId }))
    userClick({ origin: origins.createNewCitationClick })
  }

  const handleCopyCitations = (citations: CitationType[]) => {
    if (citations && citations.length) {
      copyAll(citations)

      const copyToastMessage =
        selectedCitations.length > 0
          ? copy.toastMessages.copyCitations(selectedCitations.length)
          : copy.toastMessages.copyAllCitations

      showSuccessToast(copyToastMessage)
      userClick({ origin: origins.copyAllCitationsClick })
    }
  }

  const handleCopyCitation = (citation: CitationType) => {
    copyAll([citation])
    showSuccessToast(copy.toastMessages.copyCitations(1))
  }

  const handleCopyPremiumCitation = (citation: CitationType) => {
    setPremiumCopyCitation(citation)
  }
  const handleCopyPremiumModalClose = () => {
    setPremiumCopyCitation(null)
  }

  const handleExportCitations = (citations: CitationType[]) => {
    userClick({ origin: origins.exportAllCitationsClick })
    setExportModalCitations(citations)
  }

  const handleChangedStyle = async (style: string) => {
    if (!style) return
    setCurrentStyle(style)
    setCurrentProjectStyleName(stylesList.find((id) => id.id === style).name)
    if (projectData) {
      const project = await updateProject({
        variables: {
          ...projectData.project,
          projectId,
          defaultStyle: style,
        },
      })

      setCurrentProject(project.data.updateProject)
    }
  }

  const editCitation = (citation: CitationType) => {
    setEditingCitation(citation)
    navigated(routes.toCitationListForm({ projectId }), { replace: true })
  }

  const handleDeleteCitations = (citations: CitationType[]) => {
    setDeleteModalCitations(citations)
  }

  const deleteCitations = async (citations: CitationType[]) => {
    return Promise.all(
      citations.map((citation) =>
        deleteCitation({
          variables: {
            projectId,
            citationId: citation.id,
          },
        })
      )
    ).then(() => {
      showSuccessToast(copy.toastMessages.deleteCitations(citations.length))
      setSelectedCitationsFromChangedCitations(citations)
      refetchCitations()
      handleDeleteCitations([])
    })
  }

  const handleComplete = (citationId: string, citationStatus: ValidatorState) => {
    if (!skipFormattedCitationQuery) {
      refetchCitations()
    }
    setEditingCitation(undefined)
    setLastUpdatedCitation({ id: citationId, status: citationStatus })
  }

  const handleCancel = () => {
    setEditingCitation(undefined)
  }

  const handleIgnoreIncomplete = () => {
    if (lastUpdatedCitation) {
      setLastUpdatedCitation({ ...lastUpdatedCitation, status: ValidatorStates.IGNORED })
    }
  }

  const handleSortCitations = (sortKey: SortKey) => {
    if (sortKey) {
      setSortCitationsKey(sortKey)
    }
  }

  const projectsExperienceRoute =
    getProjectsExperience() === CITATIONS_DASHBOARD
      ? routes.toCitationsDashboard()
      : routes.toFolders()
  const breadcrumbs: BreadcrumbPart[] = [
    {
      label: CITATION_BREADCRUMB_PLURAL,
      route: projectsExperienceRoute,
    },
    {
      label: CITATION_BREADCRUMB_SINGLE,
    },
  ]

  const isLoading = isNew ? false : !projectData || isCitationsLoading

  const hasCitations = formattedCitationsDataV2?.citations?.result?.length > 0

  const citations: CitationType[] = (() => {
    const mappedCitations = mapFormattedCitationsV2toCitation(
      formattedCitationsDataV2,
      currentStyle,
      projectId,
      lastUpdatedCitation
    )
    return sortCitationsByKey(mappedCitations, sortCitationsKey)
  })()

  const toggleSelectedCitation = (id: string) => {
    const selectedCitationsCopy = [...selectedCitations]
    const citationIndex = selectedCitationsCopy.findIndex((c) => c.id === id)
    const isCurrentlySelected = citationIndex > -1

    if (isCurrentlySelected) {
      selectedCitationsCopy.splice(citationIndex, 1)
      setSelectedCitations([...selectedCitationsCopy])
    } else {
      const selectedCitation = citations.find((c) => c.id === id)
      setSelectedCitations([...selectedCitationsCopy, selectedCitation])
    }
  }

  const selectAllCitations = () => {
    setSelectedCitations(citations)
  }

  const deselectAllCitations = () => {
    setSelectedCitations([])
  }

  const projectName = isNew ? newProjectName() : projectData?.project?.name

  if (projectError) {
    const statusCode = getIsNotFoundErrorByMessage(
      projectError,
      ERROR_MESSAGE_PROJECT_NOT_FOUND
    )
      ? 404
      : 500
    if (statusCode === 404) {
      navigated(projectsExperienceRoute)
      return null
    }
    return <Error statusCode={statusCode} />
  }

  return (
    <>
      <Styled.CitationListContainerWrapper>
        <nav aria-label="breadcrumb">
          <Breadcrumbs parts={breadcrumbs} />
        </nav>
        <Styled.CitationListBoxContainer>
          <PageHeader>{projectName}</PageHeader>
          {updateMessagingFeatureEnabled && <SubHeadingBCRest />}
          <Styled.CitationBox>
            <CitationListCTAContainer
              copy={copy}
              currentCitationStyle={styleIdToAlias(currentStyle)}
              currentCitationStyleName={currentStyleName}
              hasCitations={hasCitations}
              onClickCreateCitation={handleClickCreateCitation}
              onClickCopyAllCitations={() => handleCopyCitations(citations)}
              onClickExportCitations={() => handleExportCitations(citations)}
              setCurrentStyle={handleChangedStyle}
            />
            <Styled.CitationListContainer>
              {isLoading && <LoadingDots message="Loading citations" />}
              {!isLoading &&
                (hasCitations ? (
                  <Styled.CitationItemsContainer>
                    <CitationListHeaderContainer
                      enableSort={enableSort}
                      copy={copy}
                      style={currentStyle}
                      onSortSelect={handleSortCitations}
                      defaultSortKey={defaultSortKey}
                    />
                    <Styled.CitationItems>
                      {citations.map((citation) => (
                        <Citation
                          key={citation.id}
                          citation={citation}
                          editCitation={editCitation}
                          deleteCitations={handleDeleteCitations}
                          ignoreIncompleteCitation={handleIgnoreIncomplete}
                          copyCitation={handleCopyCitation}
                          openModal={NOOP}
                          isPro
                          isFootnote={isFootnote}
                          copyPremiumCitation={handleCopyPremiumCitation}
                          toggleCitationCheckbox={toggleSelectedCitation}
                          isSelected={selectedCitations.some((c) => c.id === citation.id)}
                          userClick={userClick}
                        />
                      ))}
                    </Styled.CitationItems>
                  </Styled.CitationItemsContainer>
                ) : (
                  <CitationListEmptyContainer copy={copy} />
                ))}
              {showPagination && (
                <RouterPagination
                  page={page}
                  setPage={setCitationPage}
                  maxPages={maxPages}
                />
              )}
            </Styled.CitationListContainer>
          </Styled.CitationBox>
        </Styled.CitationListBoxContainer>
      </Styled.CitationListContainerWrapper>
      <CitationFlowDrawer
        step={flowStep}
        projectId={projectId}
        editingCitation={editingCitation}
        citationStyle={currentStyle || initialStyle}
        initialSourceType={initialSourceType}
        initialSearchQuery={initialSearchQuery}
        onComplete={handleComplete}
        onCancel={handleCancel}
      />
      <SuccessToast message={toastState.message} onClear={hideToast} />
      {exportModalCitations && exportModalCitations.length > 0 && (
        <ExportModal
          citations={citations}
          selectedCitations={exportModalCitations}
          handleClose={() => {
            setExportModalCitations([]) // this closes the modal
          }}
          handleComplete={(exportResult: ExportResult) => {
            switch (exportResult.type) {
              case ExportType.export:
                showSuccessToast(copy.toastMessages.downloadCitations)
                break
              case ExportType.move:
                showSuccessToast(
                  copy.toastMessages.moveCitations(
                    exportResult.citations.length,
                    exportResult.projectName,
                    routes.toCitationList({ projectId: exportResult.projectId })
                  )
                )
                setSelectedCitationsFromChangedCitations(exportResult.citations)
                refetchCitations()
                break
              default:
                break
            }
          }}
          exportTabIds={exportTabIds}
          isLoggedIn={isLoggedIn}
          isPro={isProUser}
          openModal={openModal}
          currentStyle={currentStyle}
          userClick={userClick}
        />
      )}
      {premiumCopyCitation && (
        <PremiumCopyModal
          citation={premiumCopyCitation}
          isFootnote={isFootnote}
          currentStyle={currentStyle}
          onClose={handleCopyPremiumModalClose}
        />
      )}
      {deleteModalCitations && deleteModalCitations.length > 0 && (
        <DeleteCitationsModal
          title={deleteCitationsTitle(deleteModalCitations.length)}
          description={deleteCitationsDescription(deleteModalCitations.length)}
          handleConfirm={() => deleteCitations(deleteModalCitations)}
          handleClose={() => setDeleteModalCitations([])}
        />
      )}
      {selectedCitations && selectedCitations.length > 0 && (
        <CheckboxMenu
          onCopy={() => handleCopyCitations(selectedCitations)}
          onExport={() => handleExportCitations(selectedCitations)}
          onDelete={() => handleDeleteCitations(selectedCitations)}
          selectAllCitations={selectAllCitations}
          totalCitations={citations.length}
          deselectAllCitations={deselectAllCitations}
          selectedCitations={selectedCitations}
          isPremium
          userClick={userClick}
        />
      )}
    </>
  )
}

const mapStateToProps = (state: AppState): CitationListStateProps => ({
  isProUser: isProSelector(state),
  isLoggedIn: loggedInSelector(state),
  initialized: initializedSelector(state),
  currentStyle: currentStyleSelector(state),
  currentStyleName: currentStyleNameSelector(state),
  stylesList: stylesSelector(state),
  updateMessagingFeatureEnabled: updateMessagingFlagSelector(state),
})

const mapDispatchToProps: CitationListDispatchProps = {
  openModal,
  navigated,
  userClick,
  setCurrentProject,
  fetchCurrentProjectStyleName,
  setCurrentProjectStyleName,
}

export default connect<CitationListStateProps, CitationListDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(CitationListContainer)
